import { useState, useEffect } from 'react';
import { X, Download, Smartphone, Share, Plus, Check } from 'lucide-react';
import { Button } from '../ui/button';
import usePWAInstall from '../../hooks/usePWAInstall';

export const PWAInstallPrompt = ({ isFirstLogin = false, onDismiss }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [installing, setInstalling] = useState(false);
  const [installed, setInstalled] = useState(false);
  
  const { 
    isInstallable, 
    promptInstall, 
    dismissPrompt, 
    shouldShowPrompt,
    showIOSInstructions,
    isIOS 
  } = usePWAInstall();

  useEffect(() => {
    // Small delay to show prompt after page load
    const timer = setTimeout(() => {
      if (shouldShowPrompt(isFirstLogin) || showIOSInstructions) {
        setIsVisible(true);
      }
    }, isFirstLogin ? 1500 : 3000);

    return () => clearTimeout(timer);
  }, [isFirstLogin, shouldShowPrompt, showIOSInstructions]);

  const handleInstall = async () => {
    setInstalling(true);
    const result = await promptInstall();
    setInstalling(false);
    
    if (result.outcome === 'accepted') {
      setInstalled(true);
      setTimeout(() => {
        setIsVisible(false);
        onDismiss?.();
      }, 2000);
    }
  };

  const handleDismiss = () => {
    dismissPrompt();
    setIsVisible(false);
    onDismiss?.();
  };

  if (!isVisible) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-300">
      <div 
        className="w-full max-w-md bg-gradient-to-b from-[#1a1a1a] to-[#0a0a0a] border border-[#333] rounded-2xl shadow-2xl overflow-hidden animate-in slide-in-from-bottom-4 duration-300"
        data-testid="pwa-install-prompt"
      >
        {/* Header */}
        <div className="relative p-6 pb-4">
          <button 
            onClick={handleDismiss}
            className="absolute top-4 right-4 p-2 text-[#888] hover:text-white transition-colors"
            data-testid="pwa-dismiss-btn"
          >
            <X size={20} />
          </button>
          
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#00CCFF] to-[#0088aa] flex items-center justify-center shadow-lg shadow-[#00CCFF]/20">
              <Smartphone size={32} className="text-white" />
            </div>
            <div>
              <h2 className="text-xl font-['Chakra_Petch'] font-bold text-white">
                {installed ? 'App Installed!' : 'Get the App'}
              </h2>
              <p className="text-[#888] text-sm font-mono">
                {installed ? 'Thanks for installing!' : 'One Voice Distribution'}
              </p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="px-6 pb-6">
          {installed ? (
            <div className="flex items-center gap-3 p-4 bg-green-500/10 border border-green-500/30 rounded-xl">
              <Check size={24} className="text-green-500" />
              <p className="text-green-400 font-mono text-sm">
                App added to your home screen!
              </p>
            </div>
          ) : showIOSInstructions ? (
            <>
              <p className="text-[#ccc] text-sm mb-4">
                Install One Voice on your iPhone for the best experience:
              </p>
              <div className="space-y-3">
                <div className="flex items-start gap-3 p-3 bg-[#1a1a1a] rounded-lg border border-[#333]">
                  <div className="w-8 h-8 rounded-full bg-[#00CCFF]/20 flex items-center justify-center flex-shrink-0">
                    <Share size={16} className="text-[#00CCFF]" />
                  </div>
                  <div>
                    <p className="text-white text-sm font-medium">1. Tap the Share button</p>
                    <p className="text-[#888] text-xs">At the bottom of Safari</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-3 bg-[#1a1a1a] rounded-lg border border-[#333]">
                  <div className="w-8 h-8 rounded-full bg-[#00CCFF]/20 flex items-center justify-center flex-shrink-0">
                    <Plus size={16} className="text-[#00CCFF]" />
                  </div>
                  <div>
                    <p className="text-white text-sm font-medium">2. Add to Home Screen</p>
                    <p className="text-[#888] text-xs">Scroll down and tap this option</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-3 bg-[#1a1a1a] rounded-lg border border-[#333]">
                  <div className="w-8 h-8 rounded-full bg-[#00CCFF]/20 flex items-center justify-center flex-shrink-0">
                    <Check size={16} className="text-[#00CCFF]" />
                  </div>
                  <div>
                    <p className="text-white text-sm font-medium">3. Tap Add</p>
                    <p className="text-[#888] text-xs">Confirm to add to home screen</p>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <>
              <p className="text-[#ccc] text-sm mb-4">
                Add One Voice to your home screen for quick access, offline listening, and a native app experience.
              </p>
              
              <div className="grid grid-cols-3 gap-3 mb-5">
                <div className="text-center p-3 bg-[#1a1a1a] rounded-xl border border-[#333]">
                  <div className="text-[#00CCFF] text-xl font-bold">⚡</div>
                  <p className="text-[#888] text-xs mt-1">Fast Access</p>
                </div>
                <div className="text-center p-3 bg-[#1a1a1a] rounded-xl border border-[#333]">
                  <div className="text-[#FFDA33] text-xl font-bold">🔔</div>
                  <p className="text-[#888] text-xs mt-1">Notifications</p>
                </div>
                <div className="text-center p-3 bg-[#1a1a1a] rounded-xl border border-[#333]">
                  <div className="text-[#00ff00] text-xl font-bold">📱</div>
                  <p className="text-[#888] text-xs mt-1">Full Screen</p>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Actions */}
        {!installed && !showIOSInstructions && (
          <div className="px-6 pb-6 flex gap-3">
            <Button
              variant="outline"
              onClick={handleDismiss}
              className="flex-1 rounded-xl border-[#333] text-[#888] hover:text-white hover:border-[#555]"
              data-testid="pwa-later-btn"
            >
              Maybe Later
            </Button>
            <Button
              onClick={handleInstall}
              disabled={installing || !isInstallable}
              className="flex-1 rounded-xl bg-gradient-to-r from-[#00CCFF] to-[#0088aa] text-white font-bold hover:opacity-90 disabled:opacity-50"
              data-testid="pwa-install-btn"
            >
              {installing ? (
                <span className="flex items-center gap-2">
                  <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Installing...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <Download size={18} />
                  Install App
                </span>
              )}
            </Button>
          </div>
        )}

        {showIOSInstructions && (
          <div className="px-6 pb-6">
            <Button
              onClick={handleDismiss}
              className="w-full rounded-xl bg-[#00CCFF] text-black font-bold hover:bg-[#00CCFF]/90"
              data-testid="pwa-got-it-btn"
            >
              Got it!
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default PWAInstallPrompt;
